import { get } from 'svelte/store';
import { devices } from 'src/stores/lists/devices';
import { DEVICE_NAME_SYS_SYNC_GEN_2_DEVICES } from 'src/constants';
import { get_gen_2_component_channel_structure, is_gen_2, real_id } from 'src/helpers/interface_helpers';
import { postReq } from 'src/services/http';
import type { Device } from 'src/types/Device';

export type NameSyncTarget = 'sys' | 'channel';

/**
 * Resolves which device-side name the app follows when name_sync is enabled.
 * Missing target defaults to 'sys' for device_name_sys device types, otherwise 'channel'.
 */
export function resolveNameSyncTarget(
	device: Pick<Device, 'type' | 'name_sync_target'>,
): NameSyncTarget {
	if (device.name_sync_target === 'sys' || device.name_sync_target === 'channel') {
		return device.name_sync_target;
	}
	return DEVICE_NAME_SYS_SYNC_GEN_2_DEVICES.includes(device.type) ? 'sys' : 'channel';
}

/**
 * True when app→device / device→app sync should use Sys.SetConfig / sys.device.name.
 */
export function shouldUseSysNameSync(device: Device): boolean {
	return (
		DEVICE_NAME_SYS_SYNC_GEN_2_DEVICES.includes(device.type) ||
		resolveNameSyncTarget(device) === 'sys'
	);
}

/**
 * Reads the device-side name that should match the app name when sync is on.
 */
export function getSyncedDeviceNameFromSettings(
	device: Device,
	settings: Record<string, any> | undefined,
): string | undefined {
	if (!settings) return undefined;

	if (shouldUseSysNameSync(device)) {
		const sysName = settings?.sys?.device?.name;
		return typeof sysName === 'string' && sysName.trim() !== '' ? sysName : undefined;
	}

	const component = get_gen_2_component_channel_structure(device);
	const channelName = settings?.[component]?.name;
	return typeof channelName === 'string' && channelName.trim() !== '' ? channelName : undefined;
}

/**
 * Maps radio selection to persisted name_sync + name_sync_target.
 */
export function syncSelectionToFlags(selection: 'off' | NameSyncTarget): {
	name_sync: boolean;
	name_sync_target?: NameSyncTarget;
} {
	if (selection === 'off') {
		return { name_sync: false };
	}
	return { name_sync: true, name_sync_target: selection };
}

/**
 * Maps stored flags to radio selection value.
 */
export function flagsToSyncSelection(
	device: Pick<Device, 'type' | 'name_sync' | 'name_sync_target'>,
): 'off' | NameSyncTarget {
	if (!device.name_sync) return 'off';
	return resolveNameSyncTarget(device);
}

/**
 * True when the configured sync target has no usable name on the device yet (null/empty).
 */
export function isNameSyncTargetEmpty(
	device: Device,
	settings: Record<string, any> | undefined,
): boolean {
	if (!settings || !device.name_sync) return false;

	const target = resolveNameSyncTarget(device);
	if (target === 'sys') {
		const name = settings?.sys?.device?.name;
		return name == null || String(name).trim() === '';
	}

	if (device.type === 'SBTR-001AEU') {
		const blutrvKey = `blutrv:${String(device.id).split('_2')[1]}`;
		const name = settings?.[blutrvKey]?.name;
		return name == null || String(name).trim() === '';
	}

	const component = get_gen_2_component_channel_structure(device);
	const name = settings?.[component]?.name;
	return name == null || String(name).trim() === '';
}

/**
 * Clears persisted name_sync when the sync target still has no name.
 * @returns true when flags were cleared
 */
export async function clearNameSyncIfTargetEmpty(
	device: Device,
	settings: Record<string, any> | undefined,
): Promise<boolean> {
	if (!device.name_sync || !isNameSyncTargetEmpty(device, settings)) return false;

	try {
		await postReq('interface/device/bulk_update', {
			devices_data: `{"${device.id}":${JSON.stringify({ name_sync: false })}}`,
		});
	} catch {
		return false;
	}

	const current = get(devices)[device.id] ?? device;
	const updated: Device = { ...current, name_sync: false };
	delete updated.name_sync_target;
	devices.update(updated);
	return true;
}

/**
 * When device settings arrive (e.g. Shelly:Settings), push the synced device-side
 * name into the app interface name for related channels. Does not call device RPCs.
 */
export async function syncAppNamesFromDeviceSettings(
	physicalId: string,
	settings: Record<string, any> | undefined,
): Promise<void> {
	if (!settings) return;

	const rid = real_id(physicalId);
	const allDevices = get(devices);

	const related = Object.values(allDevices).filter(
		(device) => device && real_id(device.id) === rid && device.name_sync && is_gen_2(device),
	);

	for (const device of related) {
		const expectedName = getSyncedDeviceNameFromSettings(device, settings);
		if (!expectedName || expectedName === device.name) continue;

		const updated: Device = { ...device, name: expectedName };
		devices.update(updated);

		try {
			await postReq('interface/device/save', {
				id: device.id,
				data: JSON.stringify(updated),
			});
		} catch {
			// Silent — reverse sync must not toast or interrupt socket handling
		}
	}
}
